/*
 * Project: bellcloud
 * File: apps/frontend/src/utils/calibrationCertificate.ts
 * Author: SWAN
 * Created: 2025-01-12
 * Description: Calibration Certificate PDF 생성 유틸리티
 */

import { jsPDF } from 'jspdf';

interface SensorData {
  sensor_name: string;
  gas_conc: number | null;
  conc_unit_text?: string;
  result: number;
  low_alarm?: number | null;
  high_alarm?: number | null;
  twa_alarm?: number | null;
  stel_alarm?: number | null;
}

interface CalibrationCertificateData {
  // Device & Operator
  serialNumber: string;
  detectorName: string;
  calibrationDueDate: string;
  operator: string;

  // Log Info
  timestamp: string;
  logType: number; // 1 = Zero Calibration, 2 = Span Calibration, 3 = Bump Test
  
  // Sensor Data
  sensors: SensorData[];

  // Additional Info
  temperature: number | null;
}

export const generateCalibrationCertificate = (data: CalibrationCertificateData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = 20;

  // Helper function to add text
  const addText = (text: string, x: number, y: number, options?: { fontSize?: number; fontStyle?: string; align?: 'left' | 'center' | 'right' }) => {
    const fontSize = options?.fontSize || 10;
    const fontStyle = options?.fontStyle || 'normal';
    const align = options?.align || 'left';

    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);
    doc.text(text, x, y, { align });
  };

  // Helper function to draw horizontal line
  const drawLine = (y: number, x1: number = margin, x2: number = pageWidth - margin) => {
    doc.setLineWidth(0.5);
    doc.line(x1, y, x2, y);
  };

  // Helper function to draw table
  const drawTable = (headers: string[], rows: string[][], startY: number, colWidths: number[]) => {
    let currentY = startY;
    const rowHeight = 8;
    const cellPadding = 2;

    // Draw header
    doc.setFillColor(220, 220, 220);
    doc.rect(margin, currentY, pageWidth - 2 * margin, rowHeight, 'F');
    
    let currentX = margin + cellPadding;
    headers.forEach((header, idx) => {
      addText(header, currentX, currentY + rowHeight - cellPadding, { fontSize: 9, fontStyle: 'bold' });
      currentX += colWidths[idx];
    });

    currentY += rowHeight;

    // Draw rows
    rows.forEach((row, rowIdx) => {
      // Alternate row background
      if (rowIdx % 2 === 0) {
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, currentY, pageWidth - 2 * margin, rowHeight, 'F');
      }

      currentX = margin + cellPadding;
      row.forEach((cell, colIdx) => {
        addText(cell, currentX, currentY + rowHeight - cellPadding, { fontSize: 9 });
        currentX += colWidths[colIdx];
      });

      currentY += rowHeight;
    });

    // Draw table border
    doc.setLineWidth(0.3);
    doc.rect(margin, startY, pageWidth - 2 * margin, (rows.length + 1) * rowHeight);

    // Draw vertical lines
    currentX = margin;
    colWidths.forEach((width, idx) => {
      if (idx < colWidths.length - 1) {
        currentX += width;
        doc.line(currentX, startY, currentX, startY + (rows.length + 1) * rowHeight);
      }
    });

    return currentY;
  };

  // Title (log_type에 따라 다르게 표시)
  const isBumpTest = data.logType === 3;
  const certificateTitle = isBumpTest ? 'Bump Test Certificate' : 'Calibration Certificate';
  addText(certificateTitle, pageWidth / 2, yPos, { fontSize: 18, fontStyle: 'bold', align: 'center' });
  yPos += 10;

  // Date
  const formattedDate = new Date(data.timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  addText(formattedDate, pageWidth / 2, yPos, { fontSize: 10, align: 'center' });
  yPos += 15;

  // Device & Operator Section
  addText('Device & Operator', margin, yPos, { fontSize: 12, fontStyle: 'bold' });
  yPos += 8;

  const dueDateLabel = isBumpTest ? 'Bump Test Due Date:' : 'Calibration Due Date:';
  const deviceOperatorData = [
    { label: 'Serial Number:', value: data.serialNumber },
    { label: 'Detector:', value: data.detectorName },
    { label: dueDateLabel, value: data.calibrationDueDate || 'N/A' },
    { label: 'Operator:', value: data.operator },
  ];

  const infoRowHeight = 8;
  const labelWidth = 60;

  deviceOperatorData.forEach((item, idx) => {
    const rowY = yPos + idx * infoRowHeight;
    // Label
    addText(item.label, margin, rowY, { fontSize: 10, fontStyle: 'bold' });
    // Value
    addText(item.value, margin + labelWidth, rowY, { fontSize: 10 });
  });

  yPos += deviceOperatorData.length * infoRowHeight + 10;

  // Sensor Section
  addText('Sensor', margin, yPos, { fontSize: 12, fontStyle: 'bold' });
  yPos += 8;

  // Determine log_type: 1 = Zero Calibration, 2 = Span Calibration, 3 = Bump Test
  const isSpan = data.logType === 2;
  const isZero = data.logType === 1;

  // Sensor table headers
  const sensorHeaders = [
    'Type',
    (isSpan || isBumpTest) ? 'Next Calibration Date:' : '',
    (isSpan || isBumpTest) ? (isSpan ? 'Span Result:' : 'Bump Test Result:') : '',
    (isSpan || isBumpTest) ? (isSpan ? 'Span Concentration:' : 'Bump Test Concentration:') : '',
    isZero ? 'Zero Result:' : '',
  ].filter(h => h !== '');

  // Sensor table rows
  const sensorRows = data.sensors.map(sensor => {
    const result = sensor.result === 1 ? 'Passed' : 'Failed';
    const concentration = sensor.gas_conc !== null 
      ? `${sensor.gas_conc} ${sensor.conc_unit_text || 'ppm'}`
      : '-';

    if (isSpan || isBumpTest) {
      return [
        sensor.sensor_name,
        'N/A',
        result,
        concentration,
      ];
    } else {
      return [
        sensor.sensor_name,
        result,
      ];
    }
  });

  const sensorColWidths = (isSpan || isBumpTest)
    ? [30, 50, 35, 50]
    : [60, 50];

  yPos = drawTable(sensorHeaders, sensorRows, yPos, sensorColWidths);
  yPos += 10;

  // Set Point Section (always display)
  addText('Set Point', margin, yPos, { fontSize: 12, fontStyle: 'bold' });
  yPos += 8;

  const setPointHeaders = ['Type:', 'Low Alarm:', 'High Alarm:', 'TWA Alarm:', 'STEL Alarm:'];
  const setPointRows = data.sensors.map(sensor => [
    sensor.sensor_name,
    sensor.low_alarm !== null && sensor.low_alarm !== undefined 
      ? `${sensor.low_alarm} ${sensor.conc_unit_text || 'ppm'}`
      : '-',
    sensor.high_alarm !== null && sensor.high_alarm !== undefined
      ? `${sensor.high_alarm} ${sensor.conc_unit_text || 'ppm'}`
      : '-',
    sensor.twa_alarm !== null && sensor.twa_alarm !== undefined
      ? `${sensor.twa_alarm} ${sensor.conc_unit_text || 'ppm'}`
      : '-',
    sensor.stel_alarm !== null && sensor.stel_alarm !== undefined
      ? `${sensor.stel_alarm} ${sensor.conc_unit_text || 'ppm'}`
      : '-',
  ]);

  const setPointColWidths = [30, 35, 35, 35, 35];
  yPos = drawTable(setPointHeaders, setPointRows, yPos, setPointColWidths);

  // Footer
  const footerY = pageHeight - 20;
  drawLine(footerY - 5);
  addText('This certificate is automatically generated by the system.', pageWidth / 2, footerY, { fontSize: 8, align: 'center' });

  // Save PDF
  const typeName = isBumpTest ? 'Bump_Test' : isSpan ? 'Span_Calibration' : 'Zero_Calibration';
  const fileName = `${isBumpTest ? 'Bump_Test' : 'Calibration'}_Certificate_${data.serialNumber}_${typeName}_${new Date(data.timestamp).getTime()}.pdf`;
  doc.save(fileName);
};

